import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import api, { currentImpersonation, actAsTenant } from "@/lib/api";
import { LayoutDashboard, Package, ClipboardCheck, FileBadge, LogOut, ShieldCheck, Users, BarChart3, Repeat, Kanban, Boxes, Gauge, GraduationCap, BookOpen, Library, Wallet, AlertTriangle, Clock, CalendarClock, FileStack, Building2, Contact2, Receipt, Layers, Eye, X as XIcon, History, ArrowLeftCircle, Settings, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import NotificationBell from "@/components/NotificationBell";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/equipment", label: "Equipment", icon: Package, testid: "nav-equipment", hideForApprover: true },
  { to: "/my-customers", label: "My Customers", icon: Contact2, testid: "nav-my-customers", inspectorOnly: true },
  { to: "/inspections", label: "Inspections", icon: ClipboardCheck, testid: "nav-inspections", hideForApprover: true },
  { to: "/yellow-cards", label: "Yellow Cards", icon: AlertTriangle, testid: "nav-yellow-cards" },
  { to: "/board", label: "Job Board", icon: Kanban, testid: "nav-board", hideForApprover: true },
  { to: "/schedules", label: "Schedules", icon: Repeat, testid: "nav-schedules", hideForApprover: true },
  { to: "/certificates", label: "Certificates", icon: FileBadge, testid: "nav-certificates" },
  { to: "/approval-sla", label: "Approval SLA", icon: Gauge, testid: "nav-approval-sla", approverAdminOnly: true },
  { to: "/learning", label: "My Learning", icon: GraduationCap, testid: "nav-learning", hideForApprover: false },
  { to: "/documents", label: "Documents", icon: Library, testid: "nav-documents" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/billing", label: "Billing", icon: Wallet, testid: "nav-billing", approverAdminOnly: true },
  { to: "/superadmin/tenants", label: "Tenants", icon: Layers, testid: "nav-tenants", superAdminOnly: true },
];

const settingsItems = [
  { to: "/customers", label: "Customer Management", icon: Contact2, testid: "nav-customers", approverAdminOnly: true },
  { to: "/assets", label: "Assets", icon: Boxes, testid: "nav-assets", hideForApprover: true },
  { to: "/training", label: "Training Admin", icon: BookOpen, testid: "nav-training", approverAdminOnly: true },
  { to: "/organizations", label: "Organisation Management", icon: Building2, testid: "nav-organizations", tenantAdminOnly: true },
  { to: "/templates", label: "Templates", icon: FileStack, testid: "nav-templates", adminOnly: true },
  { to: "/admin/users", label: "User Management", icon: Users, testid: "nav-users", adminOnly: true },
  { to: "/audit-logs", label: "Audit Log", icon: History, testid: "nav-audit-logs", approverAdminOnly: true },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(true);

  const isVisible = (n) =>
    (!n.adminOnly || ["admin", "super_admin"].includes(user?.role)) &&
    (!n.tenantAdminOnly || user?.role === "admin") &&
    (!n.superAdminOnly || user?.role === "super_admin") &&
    (!n.approverAdminOnly || ["admin", "super_admin"].includes(user?.role) || user?.role === "approver") &&
    (!n.inspectorOnly || user?.role === "inspector" || ["admin", "super_admin"].includes(user?.role)) &&
    !(user?.role === "approver" && n.hideForApprover);

  const settingsVisible = settingsItems.filter(isVisible);

  const renderLink = ({ to, label, icon: Icon, testid }) => (
    <NavLink
      key={to}
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 text-sm font-medium transition-colors ${
          isActive ? "bg-[#01ADEF] text-white" : "text-gray-700 hover:bg-gray-100"
        }`
      }
    >
      <Icon className="w-4 h-4" strokeWidth={2} />
      {label}
    </NavLink>
  );

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col" data-testid="app-sidebar">
        <div className="h-16 px-5 flex items-center gap-2 border-b border-gray-200">
          <img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_cert-hub-31/artifacts/shlxk4d2_image-1.png" alt="Doc Q" className="w-9 h-9 object-contain" />
          <span className="font-display text-lg font-bold tracking-tight">Doc Q</span>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {navItems.filter(isVisible).map(renderLink)}
          {settingsVisible.length > 0 && (
            <div className="pt-2 mt-2 border-t border-gray-100">
              <button
                data-testid="nav-settings-group"
                onClick={() => setSettingsOpen((o) => !o)}
                className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
              >
                <Settings className="w-4 h-4" strokeWidth={2} />
                <span className="flex-1 text-left">Settings</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${settingsOpen ? "" : "-rotate-90"}`} />
              </button>
              {settingsOpen && (
                <div className="mt-1 space-y-1 pl-3" data-testid="settings-group-items">
                  {settingsVisible.map(renderLink)}
                </div>
              )}
            </div>
          )}
        </nav>
        <div className="border-t border-gray-200 p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-gray-900 text-white flex items-center justify-center text-xs font-bold">
              {(user?.name || "U").slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-xs text-gray-500 uppercase tracking-wider" data-testid="user-role">{user?.role}</div>
            </div>
          </div>
          <Button
            data-testid="account-link"
            variant="outline"
            size="sm"
            onClick={() => navigate("/account")}
            className="w-full rounded-full border-gray-300 mb-2"
          >
            <ShieldCheck className="w-3.5 h-3.5 mr-2" /> Account & Security
          </Button>
          <Button
            data-testid="logout-button"
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="w-full rounded-full border-gray-300"
          >
            <LogOut className="w-3.5 h-3.5 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto flex flex-col">
        <ReturnToSuperAdminBanner user={user} />
        <ImpersonationBanner user={user} />
        <div className="h-14 border-b border-gray-200 bg-white px-4 flex items-center justify-end shrink-0">
          <NotificationBell />
        </div>
        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export function ReturnToSuperAdminBanner({ user }) {
  const [busy, setBusy] = useState(false);
  if (!user?.can_return_to_super_admin) return null;
  const goBack = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/return-to-super-admin");
      toast.success(`Welcome back, ${data.user.email}`);
      setTimeout(() => { window.location.href = "/superadmin/tenants"; }, 300);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to switch back");
      setBusy(false);
    }
  };
  return (
    <div className="bg-[#EFF6FF] border-b border-[#93C5FD] text-[#1E3A8A] px-4 py-2 flex items-center justify-between text-xs font-semibold" data-testid="return-to-super-banner">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-3.5 h-3.5" />
        You&apos;re signed in as this tenant on behalf of <b>{user.return_to_super_email}</b>. Actions affect the tenant workspace directly.
      </div>
      <button
        onClick={goBack}
        disabled={busy}
        data-testid="return-to-super-btn"
        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white border border-[#93C5FD] hover:bg-[#DBEAFE] disabled:opacity-50"
      >
        <ArrowLeftCircle className="w-3.5 h-3.5" /> {busy ? "Switching…" : "Return to Super-Admin"}
      </button>
    </div>
  );
}

function ImpersonationBanner({ user }) {
  const [tenant, setTenant] = useState(null);
  const impersonating = currentImpersonation();
  useEffect(() => {
    if (user?.role === "super_admin" && impersonating) {
      api.get("/me/tenant").then(r => setTenant(r.data?.tenant)).catch(() => {});
    } else {
      setTenant(null);
    }
  }, [user, impersonating]);
  if (!impersonating || user?.role !== "super_admin") return null;
  return (
    <div className="bg-[#FEF3C7] border-b border-[#FCD34D] text-[#92400E] px-4 py-2 flex items-center justify-between text-xs font-semibold" data-testid="global-impersonation-banner">
      <div className="flex items-center gap-2">
        <Eye className="w-3.5 h-3.5" />
        Viewing <b>{tenant?.name || "a tenant"}</b> in <b>read-only mode</b> — you can browse but not modify.
        <span className="text-[10px] font-normal text-[#92400E]/80 hidden md:inline">To edit, use &ldquo;Sign in as tenant admin&rdquo;.</span>
      </div>
      <button onClick={() => { actAsTenant(null); window.location.reload(); }} className="inline-flex items-center gap-1 hover:underline" data-testid="global-stop-impersonation">
        <XIcon className="w-3.5 h-3.5" /> Stop
      </button>
    </div>
  );
}
